import type { UnknownAction } from 'redux';
import type { BasicBooking } from '../../common/graphql/fragments/gql/BasicBooking';
import type { DetailedIntermodalBooking } from '../../common/graphql/fragments/gql/IntermodalBooking';
import type { Sailing } from '../../common/graphql/fragments/gql/Sailing';
import type { UrgentMessage } from '../../common/graphql/fragments/gql/UrgentMessage';
import type { CreatedAt } from './notificationsReducer';
import { createCreatedAt } from './notificationsUtils';

export const generateBookingResultId = () => Date.now();

export type NotificationsActions =
  | AddBookingResultNotificationAction
  | RemoveBookingResultNotificationAction
  | AddUrgentMessageNotificationAction
  | MarkUrgentMessageAsReadAction
  | MarkUrgentMessageAsUnreadAction;

export type BookingResult =
  | {
      type: 'cancel-success';
      bookingNo: number;
      messages: string[];
      warnings: string[];
    }
  | {
      type: 'cancel-fail';
      bookingNo: number;
      errors: string[];
    }
  | {
      type: 'waitlist';
      bookings: BasicBooking[];
      sailing: Sailing | null;
      messages: string[];
      warnings: string[];
    }
  | {
      type: 'switch-success';
      sailing: Sailing;
      confirmedBookingNo: number;
      waitlistedBookingNo: number;
      messages: string[];
      warnings: string[];
    }
  | {
      type: 'switch-fail';
      confirmedBookingNo: number;
      waitlistedBookingNo: number;
      errors: string[];
    }
  | {
      type: 'create-fail';
      count: number;
      sailing: Sailing | null;
      errors: string[];
      warnings: string[];
    }
  | {
      type: 'confirmed';
      bookings: BasicBooking[];
      sailing: Sailing | null;
      messages: string[];
      warnings: string[];
    };

export type IntermodalResult =
  | {
      type: 'intermodal-cancel-success';
      bookingNo: number;
      messages: string[];
      warnings: string[];
    }
  | {
      type: 'intermodal-cancel-fail';
      bookingNo: number;
      errors: string[];
    }
  | {
      type: 'intermodal-created';
      booking: DetailedIntermodalBooking;
      messages: string[];
      warnings: string[];
    };

export type NotificationResult = BookingResult | IntermodalResult;

export interface AddBookingResultNotificationAction extends UnknownAction {
  payload: {
    createdAt: CreatedAt;
    bookingResult: NotificationResult;
    id: number;
  };
  type: 'NOTIFICATION:ADD_BOOKING_RESULT_NOTIFICATION';
}

export const addBookingResultNotification = (
  bookingResult: NotificationResult,
  createdAt: CreatedAt = createCreatedAt(),
  id: number = generateBookingResultId(),
): AddBookingResultNotificationAction => ({
  payload: {
    createdAt,
    bookingResult,
    id,
  },
  type: 'NOTIFICATION:ADD_BOOKING_RESULT_NOTIFICATION',
});

export interface RemoveBookingResultNotificationAction extends UnknownAction {
  payload: {
    id: number;
  };
  type: 'NOTIFICATION:REMOVE_BOOKING_RESULT_NOTIFICATION';
}

export const removeBookingResultNotification = (
  id: number,
): RemoveBookingResultNotificationAction => ({
  payload: {
    id,
  },
  type: 'NOTIFICATION:REMOVE_BOOKING_RESULT_NOTIFICATION',
});

export interface AddUrgentMessageNotificationAction extends UnknownAction {
  payload: {
    createdAt: CreatedAt;
    message: UrgentMessage;
  };
  type: 'NOTIFICATION:ADD_URGENT_MESSAGE_NOTIFICATION';
}

export const addUrgentMessageNotification = (
  message: UrgentMessage,
  createdAt: CreatedAt,
): AddUrgentMessageNotificationAction => ({
  payload: {
    createdAt,
    message,
  },
  type: 'NOTIFICATION:ADD_URGENT_MESSAGE_NOTIFICATION',
});

export interface MarkUrgentMessageAsUnreadAction extends UnknownAction {
  payload: {
    id: string;
  };
  type: 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_UNREAD';
}

export const markUrgentMessageAsUnread = (id: string): MarkUrgentMessageAsUnreadAction => ({
  payload: {
    id,
  },
  type: 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_UNREAD',
});

export interface MarkUrgentMessageAsReadAction extends UnknownAction {
  payload: {
    id: string;
  };
  type: 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_READ';
}

export const markUrgentMessageAsRead = (id: string): MarkUrgentMessageAsReadAction => ({
  payload: {
    id,
  },
  type: 'NOTIFICATIONS:MARK_URGENT_MESSAGE_AS_READ',
});
